import type { BacktestConfig, BacktestResult } from "../types";

interface BacktestPanelProps {
  config: BacktestConfig;
  onConfigChange: (patch: Partial<BacktestConfig>) => void;
  onRun: () => void;
  result: BacktestResult | null;
  error: string | null;
}

function Field({
  label,
  value,
  step = "any",
  onChange,
}: {
  label: string;
  value: number;
  step?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-tv-muted">
      {label}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isNaN(n)) onChange(n);
        }}
        className="rounded border border-tv-border bg-tv-bg px-2 py-1.5 text-sm text-tv-text outline-none focus:border-tv-blue"
      />
    </label>
  );
}

function Metric({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
}) {
  const color =
    tone === "positive"
      ? "text-tv-green"
      : tone === "negative"
        ? "text-tv-red"
        : "text-tv-text";
  return (
    <div className="rounded border border-tv-border bg-tv-panel px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-tv-muted">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-semibold ${color}`}>{value}</div>
    </div>
  );
}

const fmtMoney = (n: number) =>
  `${n >= 0 ? "+" : ""}${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n: number) =>
  Number.isFinite(n) ? `${n.toFixed(2)}%` : "∞";

export default function BacktestPanel({
  config,
  onConfigChange,
  onRun,
  result,
  error,
}: BacktestPanelProps) {
  const m = result?.metrics;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Configuration bar ── */}
      <div className="flex flex-wrap items-end gap-3 border-b border-tv-border bg-tv-panel px-4 py-3">
        <Field
          label="Balance $"
          value={config.initialBalance}
          onChange={(v) => onConfigChange({ initialBalance: v })}
        />
        <Field
          label="Lots"
          value={config.lots}
          step="0.01"
          onChange={(v) => onConfigChange({ lots: v })}
        />
        <Field
          label="SL $"
          value={config.slDistance}
          step="0.1"
          onChange={(v) => onConfigChange({ slDistance: v })}
        />
        <Field
          label="TP $"
          value={config.tpDistance}
          step="0.1"
          onChange={(v) => onConfigChange({ tpDistance: v })}
        />
        <Field
          label="Spread $"
          value={config.spread}
          step="0.01"
          onChange={(v) => onConfigChange({ spread: v })}
        />
        <Field
          label="Warmup"
          value={config.warmupBars}
          onChange={(v) => onConfigChange({ warmupBars: v })}
        />
        <button
          onClick={onRun}
          className="ml-auto rounded bg-tv-blue px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 active:scale-[0.98]"
        >
          ▶ Run Backtest
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="border-b border-tv-border bg-red-950/40 px-4 py-2 font-mono text-xs text-tv-red">
          {error}
        </div>
      )}

      {/* ── Results ── */}
      {!result && !error && (
        <div className="flex flex-1 items-center justify-center text-sm text-tv-muted">
          Configure parameters and press <span className="mx-1 text-tv-blue">Run Backtest</span> to analyze
          the strategy.
        </div>
      )}

      {m && result && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            <Metric
              label="Net Profit"
              value={`$${fmtMoney(m.netProfit)}`}
              tone={m.netProfit >= 0 ? "positive" : "negative"}
            />
            <Metric label="Return" value={fmtPct(m.netProfitPct)} tone={m.netProfitPct >= 0 ? "positive" : "negative"} />
            <Metric
              label="Win Rate"
              value={fmtPct(m.winRate)}
              tone={m.winRate >= 50 ? "positive" : "negative"}
            />
            <Metric
              label="Profit Factor"
              value={m.profitFactor === Infinity ? "∞" : m.profitFactor.toFixed(2)}
              tone={m.profitFactor >= 1 ? "positive" : "negative"}
            />
            <Metric
              label="Max Drawdown"
              value={`$${m.maxDrawdown.toFixed(2)} (${m.maxDrawdownPct.toFixed(2)}%)`}
              tone="negative"
            />
            <Metric
              label="Sharpe"
              value={m.sharpeRatio.toFixed(2)}
              tone={m.sharpeRatio >= 1 ? "positive" : "neutral"}
            />
            <Metric label="Trades" value={`${m.totalTrades} (W${m.wins}/L${m.losses}/B${m.breakeven})`} />
            <Metric label="Expectancy" value={`$${m.expectancy.toFixed(2)}/trade`} tone={m.expectancy >= 0 ? "positive" : "negative"} />
            <Metric label="Max Consec. Losses" value={String(m.maxConsecutiveLosses)} tone={m.maxConsecutiveLosses > 4 ? "negative" : "neutral"} />
            <Metric label="Avg Win" value={`$${m.avgWin.toFixed(2)}`} tone="positive" />
            <Metric label="Avg Loss" value={`$${m.avgLoss.toFixed(2)}`} tone="negative" />
            <Metric label="Avg Bars Held" value={m.avgBarsHeld.toFixed(1)} />
          </div>

          {/* ── Trade journal ── */}
          <div className="mt-4 overflow-hidden rounded border border-tv-border">
            <div className="border-b border-tv-border bg-tv-panel px-3 py-2 text-[11px] uppercase tracking-wider text-tv-muted">
              Trade Journal ({result.trades.length})
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-left font-mono text-xs">
                <thead className="sticky top-0 bg-tv-panel text-tv-muted">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">#</th>
                    <th className="px-3 py-1.5 font-medium">Side</th>
                    <th className="px-3 py-1.5 font-medium">Entry</th>
                    <th className="px-3 py-1.5 font-medium">Exit</th>
                    <th className="px-3 py-1.5 font-medium">PnL $</th>
                    <th className="px-3 py-1.5 font-medium">Exit</th>
                    <th className="px-3 py-1.5 font-medium">Bars</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trades.map((t) => (
                    <tr key={t.id} className="border-t border-tv-border/50 hover:bg-tv-panel/60">
                      <td className="px-3 py-1.5 text-tv-muted">{t.id}</td>
                      <td className={`px-3 py-1.5 font-semibold ${t.action === "BUY" ? "text-tv-green" : "text-tv-red"}`}>
                        {t.action}
                      </td>
                      <td className="px-3 py-1.5">{t.entryPrice.toFixed(2)}</td>
                      <td className="px-3 py-1.5">{t.exitPrice.toFixed(2)}</td>
                      <td className={`px-3 py-1.5 font-semibold ${t.pnl >= 0 ? "text-tv-green" : "text-tv-red"}`}>
                        {fmtMoney(t.pnl)}
                      </td>
                      <td className="px-3 py-1.5 text-tv-muted">{t.exitReason}</td>
                      <td className="px-3 py-1.5 text-tv-muted">{t.barsHeld}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
